import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MetricCard } from '@/components/MetricCard';
import { AreaChart, GaugeChart, ChartContainer } from '@/components/charts';
import { useAccountDashboard, useAccountHealthHistory } from '../hooks';
import {
  RefreshCw, ArrowLeft, Thermometer, AlertTriangle,
  CheckCircle, CreditCard, HeartHandshake, Headphones, Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';

function getHealthStatus(score: number | null | undefined) {
  if (score == null) return 'critical';
  if (score >= 80) return 'healthy';
  if (score >= 50) return 'warning';
  return 'critical';
}

function getHealthColor(status: string) {
  switch (status) {
    case 'healthy': return 'text-green-600';
    case 'warning': return 'text-yellow-600';
    case 'critical': return 'text-red-600';
    default: return 'text-gray-600';
  }
}

export default function OrganisationDetailPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const { t } = useTranslation('intelligence');
  const { data, isLoading, error, isFetching, refetch } = useAccountDashboard(customerId || '');
  const { data: historyData, isLoading: isHistoryLoading } = useAccountHealthHistory(customerId || '', 30);

  const account = data?.account;
  const health = data?.healthMetrics;

  const healthStatus = getHealthStatus(health?.healthScore);

  const healthTrendData = (historyData?.history || []).map((h) => ({
    name: new Date(h.date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
    value: h.healthScore ?? 0,
  }));

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-red-700">{t('common:error')}: {error.message}</span>
          <Button variant="outline" onClick={() => refetch()}>
            {t('common:retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/intelligence/customers">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            {isLoading ? (
              <Skeleton className="h-8 w-48" />
            ) : (
              <h1 className="text-2xl font-bold text-gray-900">
                {account?.name}
              </h1>
            )}
            <p className="text-sm text-gray-500 mt-1">{t('organisation.subtitle')}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          {t('common:refresh')}
        </Button>
      </div>

      {/* Health Score Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>{t('organisation.healthScore')}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[200px] w-full" />
            ) : (
              <div className="text-center">
                <GaugeChart
                  value={health?.healthScore || 0}
                  max={100}
                  height={180}
                  suffix="%"
                />
                <Badge className={cn(
                  'mt-4 text-sm',
                  healthStatus === 'healthy' && 'bg-green-100 text-green-800',
                  healthStatus === 'warning' && 'bg-yellow-100 text-yellow-800',
                  healthStatus === 'critical' && 'bg-red-100 text-red-800',
                )}>
                  {t(`customers.status.${healthStatus}`)}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('organisation.healthTrend')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={isLoading || isHistoryLoading} height={200}>
              <AreaChart
                data={healthTrendData}
                series={[{ dataKey: 'value', name: 'Health Score', color: '#3b82f6' }]}
                height={180}
              />
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Health Factors */}
      <Card>
        <CardHeader>
          <CardTitle>{t('organisation.healthFactors')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { key: 'subscription', value: health?.subscriptionHealthScore, weight: 30, icon: CreditCard, color: 'blue' },
              { key: 'payment', value: health?.paymentHealthScore, weight: 35, icon: HeartHandshake, color: 'orange' },
              { key: 'engagement', value: health?.engagementHealthScore, weight: 20, icon: Activity, color: 'green' },
              { key: 'support', value: health?.supportHealthScore, weight: 15, icon: Headphones, color: 'purple' },
            ].map((factor) => (
              <div key={factor.key} className="space-y-3">
                <div className="flex items-center gap-2">
                  <factor.icon className={cn(
                    'h-5 w-5',
                    factor.color === 'blue' && 'text-blue-600',
                    factor.color === 'orange' && 'text-orange-600',
                    factor.color === 'green' && 'text-green-600',
                    factor.color === 'purple' && 'text-purple-600',
                  )} />
                  <span className="text-sm font-medium text-gray-700">
                    {t(`customers.factors.${factor.key}`)}
                  </span>
                </div>
                {isLoading ? (
                  <Skeleton className="h-4 w-full" />
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Weight: {factor.weight}%</span>
                      <span className={cn(
                        'font-semibold',
                        getHealthColor(getHealthStatus(factor.value))
                      )}>
                        {factor.value != null ? `${factor.value}` : '--'}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          (factor.value || 0) >= 80 && 'bg-green-500',
                          (factor.value || 0) >= 50 && (factor.value || 0) < 80 && 'bg-yellow-500',
                          (factor.value || 0) < 50 && 'bg-red-500',
                        )}
                        style={{ width: `${factor.value || 0}%` }}
                      />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={t('organisation.metrics.activeSensors')}
          value={health?.activeSensorsCount}
          icon={<Thermometer className="h-5 w-5" />}
          color="green"
          loading={isLoading}
        />
        <MetricCard
          title={t('organisation.metrics.openAlerts')}
          value={health?.openTicketsCount}
          icon={<AlertTriangle className="h-5 w-5" />}
          color="red"
          loading={isLoading}
        />
        <MetricCard
          title={t('organisation.metrics.activeSubscriptions')}
          value={health?.activeSubscriptionsCount}
          icon={<CreditCard className="h-5 w-5" />}
          color="blue"
          loading={isLoading}
        />
        <MetricCard
          title={t('organisation.metrics.totalSites')}
          value={health?.totalSitesCount}
          icon={<CheckCircle className="h-5 w-5" />}
          color="purple"
          loading={isLoading}
        />
      </div>
    </div>
  );
}
